import React, { useEffect, useState } from 'react';
import { getEntityPreview } from '../../services/cms/getEntityPreview';

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  timeZone: 'Pacific/Honolulu'
});

const EXACT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
  timeZone: 'Pacific/Honolulu'
});

const ItineraryDayCard = ({
  day,
  isCollapsed = false,
  onToggle,
  canEdit = false,
  canRegenerate = true,
  isRegenerating = false,
  onRegenerate,
  onViewHistory
}) => {
  const [openReasons, setOpenReasons] = useState({});
  const [openEntityByBlock, setOpenEntityByBlock] = useState({});
  const [entityDetails, setEntityDetails] = useState({});
  const [entityLoading, setEntityLoading] = useState({});
  const [entityErrors, setEntityErrors] = useState({});

  useEffect(() => {
    if (isCollapsed) {
      setOpenReasons({});
      setOpenEntityByBlock({});
    }
  }, [isCollapsed]);

  useEffect(() => {
    setOpenReasons({});
    setOpenEntityByBlock({});
    setEntityDetails({});
    setEntityLoading({});
    setEntityErrors({});
  }, [day]);

  if (!day) return null;

  let dateSuffix = null;
  if (day.calendarDate) {
    try {
      const date = new Date(`${day.calendarDate}T00:00:00-10:00`);
      if (!Number.isNaN(date.getTime())) {
        dateSuffix = day.calendarPrecision === 'exact'
          ? EXACT_DATE_FORMATTER.format(date)
          : WEEKDAY_FORMATTER.format(date);
      }
    } catch {
      // Ignore parsing errors and fall back to no suffix
    }
  }

  const blocks = Array.isArray(day.blocks) ? day.blocks : [];
  const iterationKind = typeof day?.iteration?.kind === 'string' ? day.iteration.kind : null;
  const showEditActions = canEdit && (typeof onRegenerate === 'function' || typeof onViewHistory === 'function');
  const contentClassName = isRegenerating ? 'opacity-60' : '';
  const cardClassName = `glass-card rounded-lg p-5 md:p-6 mb-6 relative ${
    isRegenerating ? 'ring-1 ring-amber-400/40 bg-amber-500/5' : ''
  }`.trim();

  return (
    <div className={cardClassName}>
      <div className={isCollapsed ? 'mb-2' : 'mb-4'}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
          {typeof onToggle === 'function' ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!isCollapsed}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${day.title || 'day'} details`}
              className="group flex items-center gap-2 text-left text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 rounded"
            >
              <span className="inline-flex items-center justify-center p-1.5 rounded-full bg-emerald-500/15 text-emerald-300 transition-colors duration-200 group-hover:bg-emerald-500/25 group-hover:text-emerald-200">
                <svg
                  className={`w-5 h-5 transform transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 6l6 6-6 6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="text-xl md:text-2xl font-bold flex items-center gap-2">
                {day.title}
                {iterationKind && iterationKind !== 'initial' && (
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                    Updated
                  </span>
                )}
              </span>
            </button>
          ) : (
            <h3 className="text-xl md:text-2xl font-bold text-white">
              {day.title}
            </h3>
          )}
          <div className="flex flex-col items-end gap-2 md:flex-row md:items-center">
            {isRegenerating && (
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-200">
                <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" />
                Updating...
              </div>
            )}
            {!isCollapsed && dateSuffix && (
              <div className="text-sm md:text-base font-normal text-emerald-300 text-right md:text-right">
                {dateSuffix}
              </div>
            )}
            {showEditActions && (
              <div className="flex items-center gap-2">
                {typeof onViewHistory === 'function' && (
                  <button
                    type="button"
                    onClick={() => onViewHistory(day.dayNumber)}
                    className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                  >
                    History
                  </button>
                )}
                {typeof onRegenerate === 'function' && (
                  <button
                    type="button"
                    onClick={() => onRegenerate(day.dayNumber)}
                    disabled={!canRegenerate || isRegenerating}
                    className={`inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-200 transition-colors hover:border-emerald-400/50 hover:bg-emerald-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 ${(!canRegenerate || isRegenerating) ? 'opacity-60 cursor-not-allowed hover:bg-emerald-500/10' : ''}`}
                    title={!canRegenerate ? 'Regeneration limit reached' : 'Regenerate this day'}
                  >
                    {isRegenerating && (
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-emerald-200/30 border-t-emerald-200" />
                    )}
                    Regenerate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={contentClassName}>
          {day.summary && (
            <p className={`leading-relaxed ${isCollapsed ? 'text-sm font-medium text-white mb-2' : 'text-white'}`}>
              {day.summary}
            </p>
          )}
          {!isCollapsed && Array.isArray(day.issues) && day.issues.length > 0 && (
            <div className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              ⚠️ {day.issues.join(' • ')}
            </div>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className={contentClassName}>
          {blocks.map((b, idx) => {
            const description = b.details;
            const reason = b.activities?.[0]?.whyForYou;
            const deepDive = Array.isArray(b.relatedEntities) ? b.relatedEntities : [];
            const isReasonOpen = !!openReasons[idx];
            const toggleReason = () => {
              setOpenReasons((prev) => ({
                ...prev,
                [idx]: !prev?.[idx],
              }));
            };
            const blockKey = typeof b.blockIndex === 'number' ? b.blockIndex : idx;
            return (
              <div
                key={idx}
                className="mt-4 pt-4 border-t border-white/10"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                  <h4 className="text-white font-semibold text-lg">{b.title || b.label}</h4>
                  {b.timeframe && (
                    <div className="text-sm text-emerald-300 font-medium whitespace-nowrap">{b.timeframe}</div>
                  )}
                </div>
                {description && (
                  <p className="text-sm text-white leading-relaxed">
                    {description}
                    {reason && (
                      <button
                        type="button"
                        onClick={toggleReason}
                        aria-label={isReasonOpen ? 'Hide why this block was recommended' : 'Show why this block was recommended'}
                        aria-pressed={isReasonOpen}
                        title="Why this made the plan"
                        className={`ml-2 inline-flex items-center justify-center px-1 text-base font-semibold align-middle text-emerald-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 ${isReasonOpen ? 'text-white' : 'hover:text-white'}`}
                        style={{ verticalAlign: 'baseline' }}
                      >
                        <span className="leading-none">?</span>
                      </button>
                    )}
                  </p>
                )}
                {reason && isReasonOpen && (
                  <p className="text-xs text-emerald-300 mt-3 italic transition-opacity duration-150">✨ {reason}</p>
                )}
                {deepDive.length > 0 && (
                  <div className="mt-4 flex flex-col items-start gap-3 md:flex-row md:flex-wrap md:gap-4">
                    {deepDive.map((entity, entityIdx) => {
                      const slug = entity?.slug || '';
                      const catalog = entity?.catalog;
                      const entityKey = `${catalog || 'unknown'}:${slug || entityIdx}`;
                      const blockOpenState = openEntityByBlock[blockKey];
                      const isEntityOpen = Array.isArray(blockOpenState)
                        ? blockOpenState.includes(entityKey)
                        : blockOpenState === entityKey;
                      const details = entityDetails[entityKey];
                      const isLoadingDetails = !!entityLoading[entityKey];
                      const loadError = entityErrors[entityKey];
                      const displayTitle = details?.title || entity?.title || entity?.matchedName || slug || 'Recommended pick';

                      const ensureDetails = () => {
                        if (!catalog || !slug) return;
                        if (entityDetails[entityKey] || entityLoading[entityKey]) return;

                        setEntityLoading((prev) => ({
                          ...prev,
                          [entityKey]: true,
                        }));

                        getEntityPreview(catalog, slug)
                          .then((result) => {
                            if (!result) return;
                            setEntityDetails((prev) => ({
                              ...prev,
                              [entityKey]: result,
                            }));
                          })
                          .catch((error) => {
                            setEntityErrors((prev) => ({
                              ...prev,
                              [entityKey]: error instanceof Error ? error.message : 'Failed to load details',
                            }));
                          })
                          .finally(() => {
                            setEntityLoading((prev) => ({
                              ...prev,
                              [entityKey]: false,
                            }));
                          });
                      };

                      const handleToggle = () => {
                        setOpenEntityByBlock((prev) => {
                          const blockState = Array.isArray(prev[blockKey]) ? prev[blockKey] : [];
                          const isAlreadyOpen = blockState.includes(entityKey);
                          const nextBlockState = isAlreadyOpen
                            ? blockState.filter((key) => key !== entityKey)
                            : [...blockState, entityKey];
                          return {
                            ...prev,
                            [blockKey]: nextBlockState,
                          };
                        });
                        if (!isEntityOpen) {
                          ensureDetails();
                        }
                      };

                      return (
                        <div
                          key={`${blockKey}-${entityKey}`}
                          className="w-full rounded-lg border border-white/10 bg-white/5 md:flex-1 md:max-w-lg"
                          style={{ minWidth: '280px', maxWidth: '420px' }}
                        >
                          <button
                            type="button"
                            onClick={handleToggle}
                            aria-expanded={isEntityOpen}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-emerald-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 hover:text-white"
                          >
                            <span>{displayTitle}</span>
                            <svg
                              className={`h-4 w-4 transform transition-transform duration-200 ${isEntityOpen ? 'rotate-180 text-white' : 'text-emerald-300 group-hover:text-white'}`}
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M12 15l-5-5h10l-5 5z" fill="currentColor" />
                            </svg>
                          </button>
                          {isEntityOpen && (
                            <div className="px-3 pb-3 md:px-4 md:pb-4">
                              {isLoadingDetails && (
                                <p className="text-sm text-white/60">Loading details…</p>
                              )}
                              {!isLoadingDetails && loadError && (
                                <p className="text-sm text-amber-300">Unable to load details right now.</p>
                              )}
                              {!isLoadingDetails && !loadError && details && (
                                <div className="space-y-3">
                                  {details.imageUrl && (
                                    <img
                                      src={details.imageUrl}
                                      alt={details.title}
                                      className="w-full rounded-md object-cover aspect-[4/3]"
                                      loading="lazy"
                                    />
                                  )}
                                  {details.summary && (
                                    <p className="text-sm leading-relaxed text-white/70">
                                      {details.summary}
                                    </p>
                                  )}
                                </div>
                              )}
                              {!isLoadingDetails && !loadError && !details && (
                                <p className="text-sm text-white/60">Details coming soon.</p>
                              )}
                              {entity.url && (
                                <a
                                  href={entity.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-3 inline-flex items-center text-sm font-semibold text-emerald-300 transition-colors hover:text-white"
                                >
                                  View full profile ↗
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isCollapsed && blocks.length > 0 && (
        <div className={`mt-2 pt-2 border-t border-white/10 ${contentClassName}`.trim()}>
          <ul className="space-y-1 list-disc list-inside text-sm text-white/80">
            {blocks.map((b, idx) => (
              <li key={idx}>
                {b.title || b.label || `Block ${idx + 1}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isCollapsed && blocks.length > 0 && (
        <p className={`mt-3 text-xs text-white/60 italic ${contentClassName}`.trim()}>
          Detailed timing, descriptions, and personalization notes are hidden in this condensed view.
        </p>
      )}
    </div>
  );
};

export default ItineraryDayCard;
